import take from "lodash-es/take";
import { observer } from "mobx-react-lite";
import type { ContentfulTeaser } from "../../common/types/cmsTypes";
import useMediaQuery from "../../common/useMediaQuery";
import { mediaQueries } from "../../common/utils";
import { isArticle, isImageLink, isImageLinkToUrl } from "../../common/utils/cmsListUtils";
import { getModuleVisibility } from "../../common/utils/segmentUtils";
import segmentStore from "../../stores/cms/segmentStore";
import ContentCardLink from "./ContentCardLink";
import EntryImageLink from "./EntryImageLink";
import TeaserListLayout from "./TeaserListLayout";
import UrlImageLink from "./UrlImageLink";

type TeaserListProps = {
   teaserList: ContentfulTeaser[];
   id: string;
   title?: string;
   maxVisible?: number;
   trackingListName?: string;
   articleTeaserView?: "Kort med ingress" | "Teaser";
};

const TeaserList = ({ teaserList, id, title, maxVisible, trackingListName, articleTeaserView = "Teaser" }: TeaserListProps) => {
   const isLargerThanSm = useMediaQuery(mediaQueries.sm);
   const isLargerThanMd = useMediaQuery(mediaQueries.md);

   const segmentedTeaserList = teaserList.filter((entry) => {
      if (isArticle(entry)) {
         return segmentStore.isArticleVisible(entry.sys.id);
      }

      if (isImageLink(entry)) {
         if (entry.fields.entry) {
            return segmentStore.isArticleVisible(entry.fields.entry.sys.id);
         }
      }

      if (isImageLinkToUrl(entry)) {
         return getModuleVisibility(entry, segmentStore.currentCustomerProperties);
      }

      return false;
   });

   const visibleTeaserList = take(segmentedTeaserList, maxVisible ?? segmentedTeaserList.length);

   const shouldShowSubtitle =
      visibleTeaserList.length === 1 || (visibleTeaserList.length === 2 && isLargerThanMd) || !isLargerThanSm;

   if (visibleTeaserList.length === 0) {
      return null;
   }

   return (
      <div>
         {title && (
            <div className="mb-4">
               <h2>{title}</h2>
            </div>
         )}
         <TeaserListLayout id={id}>
            {visibleTeaserList.map((entry) => {
               if (articleTeaserView === "Kort med ingress") {
                  return (
                     <ContentCardLink
                        entry={entry}
                        key={`content-card-link-${entry.sys.id}`}
                        trackingListName={trackingListName}
                     />
                  );
               }
               if (isArticle(entry)) {
                  if (entry.fields.headerImage) {
                     const ctaTitle = entry.fields.callToAction ?? entry.fields.title;

                     return (
                        <EntryImageLink
                           key={`cta-${entry.sys.id}`}
                           id={entry.sys.id}
                           type="article"
                           imageWithFocusId={entry.fields.headerImage.sys.id}
                           title={ctaTitle}
                           subTitle={entry.fields.callToActionSubTitle}
                           trackingListName={trackingListName}
                        />
                     );
                  }
               } else if (isImageLink(entry)) {
                  const entryImageLink = entry.fields.entry;
                  const image = entry.fields.image;

                  if (entryImageLink && image) {
                     const subTitle = shouldShowSubtitle ? entry.fields.caption : "";

                     return (
                        <EntryImageLink
                           key={`image-link-${entryImageLink.sys.id}`}
                           id={entryImageLink.sys.id}
                           type="article"
                           imageWithFocusId={image.sys.id}
                           title={entry.fields.text}
                           subTitle={subTitle}
                           trackingListName={trackingListName}
                        />
                     );
                  }
               } else if (isImageLinkToUrl(entry)) {
                  const image = entry.fields.image;
                  const url = entry.fields.url;
                  const linkTitle = entry.fields.text;
                  const subTitle = shouldShowSubtitle ? entry.fields.caption : "";

                  if (image) {
                     return (
                        <UrlImageLink
                           key={`image-link-to-url-${entry.sys.id}`}
                           imageWithFocusId={image.sys.id}
                           title={linkTitle}
                           subTitle={subTitle}
                           url={url}
                           trackingListName={trackingListName}
                        />
                     );
                  }
               } else {
                  return null;
               }
            })}
         </TeaserListLayout>
      </div>
   );
};

export default observer(TeaserList);
